import React from "react";
import BespokeWizard from "@/components/bespoke/BespokeWizard";
import { BESPOKE_CATEGORIES } from "@/lib/bespokeCategories";
import {
  FitIcon, SleeveIcon, PocketIcon, CollarIcon, PlacketIcon, BottomCutIcon, CuffIcon,
  ShirtTypeIcon, UpperBodyIcon, ShoulderTypeIcon,
} from "@/components/bespoke/ShirtIcons";

const SHIRT_TYPES = [{ key: "tuxedo", label: "Tuxedo", price: 200 }, { key: "plain", label: "Plain", price: 0 }, { key: "hunter", label: "Hunter", price: 599 }];
const CUFF_TYPES = [{ key: "standard", label: "Standard", price: 0 }, { key: "french-fold", label: "French Fold", price: 200 }];
const FIT_OPTIONS = [
  { key: "slim", label: "Super Slim", help: "A close, tapered cut through the body." },
  { key: "structured", label: "Structured", help: "Tailored through the chest with a little room to move." },
  { key: "relaxed", label: "Relaxed", help: "A looser, more comfortable through-body cut." },
];
const SLEEVE_OPTIONS = [{ key: "long", label: "Long Sleeves" }, { key: "short", label: "Short Sleeves" }];
const POCKET_OPTIONS = [
  { key: "none", label: "No Pocket" }, { key: "single", label: "Single Pocket" }, { key: "double", label: "Double Pocket" },
  { key: "single-flap", label: "Single Pocket with Flap" }, { key: "double-flap", label: "Double Pocket with Flap" },
];
const COLLAR_OPTIONS = [
  { key: "standard", label: "Standard" }, { key: "button-down", label: "Button Down" }, { key: "chinese", label: "Chinese" },
  { key: "cutaway", label: "Cutaway" }, { key: "peak-lapel", label: "Peak Lapel Collar" },
];
const PLACKET_OPTIONS = [{ key: "with", label: "With Placket" }, { key: "without", label: "Without Placket" }];
const BOTTOM_CUT_OPTIONS = [{ key: "round", label: "Round" }, { key: "straight", label: "Straight" }];
const UPPER_BODY_OPTIONS = [
  { key: "athletic", label: "Athletic", help: "Broader shoulders, narrower waist." },
  { key: "average", label: "Average", help: "Balanced, proportionate build." },
  { key: "healthy", label: "Healthy", help: "Fuller through the chest and midsection." },
];
const SHOULDER_OPTIONS = [{ key: "average", label: "Average", help: "Level shoulder line." }, { key: "sloping", label: "Sloping", help: "Shoulders angle downward from the neck." }];

const config = {
  garmentType: "shirt",
  title: "Bespoke Shirt",
  prefix: "shirt",
  guideCaption: "For optimal fit, we recommend measuring from your best-fitting garment.",
  faqs: BESPOKE_CATEGORIES.shirt.faqs,
  groups: [
    { group: "upper_body_shape", label: "Upper Body Shape *", options: UPPER_BODY_OPTIONS, Icon: UpperBodyIcon, default: "average" },
    { group: "shoulder_type", label: "Shoulder Type *", options: SHOULDER_OPTIONS, Icon: ShoulderTypeIcon, default: "average", cols: "grid-cols-2", maxWidth: "max-w-sm" },
    { group: "shirt_type", label: "Shirt Type", options: SHIRT_TYPES, Icon: ShirtTypeIcon, default: "plain", maxWidth: "max-w-xl", showPrice: true, addon: true },
    { group: "fit", label: "Fit", options: FIT_OPTIONS, Icon: FitIcon, default: "structured" },
    { group: "sleeve", label: "Sleeves", options: SLEEVE_OPTIONS, Icon: SleeveIcon, default: "long", cols: "grid-cols-2", maxWidth: "max-w-sm" },
    { group: "pocket", label: "Pocket", options: POCKET_OPTIONS, Icon: PocketIcon, default: "single", cols: "grid-cols-3 md:grid-cols-5", maxWidth: "max-w-2xl", compact: true },
    { group: "collar", label: "Collar", options: COLLAR_OPTIONS, Icon: CollarIcon, default: "standard", maxWidth: "max-w-xl" },
    { group: "placket", label: "Placket", options: PLACKET_OPTIONS, Icon: PlacketIcon, default: "with", cols: "grid-cols-2", maxWidth: "max-w-sm" },
    { group: "bottom_cut", label: "Bottom Cut", options: BOTTOM_CUT_OPTIONS, Icon: BottomCutIcon, default: "round", cols: "grid-cols-2", maxWidth: "max-w-sm" },
    { group: "cuff", label: "Cuff", options: CUFF_TYPES, Icon: CuffIcon, default: "standard", cols: "grid-cols-2", maxWidth: "max-w-sm", showPrice: true, addon: true },
  ],
  sizes: ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"],
  sizeChart: {
    columns: ["Chest (in)", "Front Length (in)", "Sleeves (in)", "Across Shoulder (in)"],
    rows: [
      { size: "S", values: [40, 26, 23, 16.5] },
      { size: "M", values: [42, 27, 23, 17] },
      { size: "L", values: [44, 28, 24, 17.5] },
      { size: "XL", values: [46, 29, 25, 19] },
      { size: "XXL", values: [48, 30, 25, 19.5] },
      { size: "3XL", values: [50, 30.5, 25, 20] },
      { size: "4XL", values: [52, 30.5, 25, 20.5] },
      { size: "5XL", values: [54, 30.5, 25, 21] },
    ],
  },
  measureFields: [
    { key: "body_height", label: "Body Height *", target: "style", required: true },
    { key: "shoulder", label: "Shoulder (in) *", target: "meas", required: true },
    { key: "chest", label: "Chest (in) *", target: "meas", required: true },
    { key: "shirt_length", label: "Shirt Length (in) *", target: "meas", required: true },
    { key: "sleeve", label: "Sleeves Length (in)", target: "meas" },
    { key: "waist", label: "Waist (in)", target: "meas" },
  ],
};

export default function BespokeShirt() {
  return <BespokeWizard config={config} />;
}
